﻿import { SetStateAction, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from "react-router-dom";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";
import { DateTime } from "luxon";
import { Autocomplete, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatFixed } from "../../utilities/format";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { DataTableFilteredRequest, useDataTable } from "../../components/DataTable/TableHook";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { StoppageCalc } from "../../DTOs/Stoppage/StoppageCalc";
import { stoppagesMutations, wasQueued } from "../../offline/stoppagesMutations";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import NestedOperationsTable from "./NestedOperationsTable";
import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { operationLocationMutations } from "../../offline/operationLocationMutations";
import { engineHoursMutations } from "../../offline/engineHoursMutations";
import { filterCachedRows, matchesText } from "../../offline/filterCachedRows";
import { mirrorPlantStoppagesForStatus } from "../../offline/patchCachedStoppages";
import { plantStatusSignature, warmPlantStoppagesCache } from "../../offline/plantStoppagesCache";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { useOperationsConfig } from "../../components/Hooks/useOperationsConfig";
import { StoppageSubTypeOption } from "../../components/Hooks/useStoppageTypes";
import { Option } from "../../DTOs/Common/OptionResponse";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { TripleShift } from "../../types/Shift";
import { FilterConfig } from "../../components/DataTable/TableFiltersElement";
import { seedMachineNameFilter, withStatusOptions } from "./operationsFilterHelpers";
import {
  OPERATIONS_DEEP_LINK_PARAMS,
  hasOperationsDeepLink,
  readOperationsDeepLink,
} from "../../utilities/helpers/operationsDeepLink";
import { Cancelled } from "../../types/ErrorTypes";
import { describeUpdatedMachines, plantIdsToRefresh } from "./operationsLiveUpdate";
import { useScrollAndPulseRow } from "../../components/Hooks/useScrollAndPulseRow";
import { ZoneMachineCell } from "../../components/ZoneChip/ZoneMachineCell";
import { machineLinkTarget } from "./machineLinkTarget";
import { useIsReadOnly } from "../../utilities/helpers/roleUtilities";
import { machineStatusDescription, machineStatusName } from "../../i18n/vocabulary";

type StatusCellProps = {
  rowId: number;
  status: string;
  onClick: (id: number) => Promise<void>;
  disabled: boolean;
};

export const StatusCell: React.FC<StatusCellProps> = ({ rowId, status, onClick, disabled }) => {
  const { t } = useTranslation("production");
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    await onClick(rowId);
    setIsLoading(false);
  };

  return (
    <button
      type="button"
      aria-label={t("operationsTable.setAvailable")}
      disabled={disabled}
      className="relative inline-flex w-full h-full items-center justify-center group bg-transparent border-none p-0 text-inherit cursor-pointer disabled:cursor-not-allowed disabled:text-theme-text-muted"
      onClick={handleClick}
    >
      {isLoading ? (
        <FontAwesomeIcon icon={faSpinner} spin className="text-theme-text transition-opacity duration-[.2s]" />
      ) : status !== "UP" && (
        <>
          <FontAwesomeIcon
            icon={faSquare}
            size="2x"
            className={`transition-opacity duration-[.2s] absolute opacity-100 ${disabled ? "" : "group-hover:opacity-0"}`}
          />
          {!disabled && (
            <FontAwesomeIcon
              icon={faSquareCheck}
              size="2x"
              className="transition-opacity duration-[.2s] absolute opacity-0 group-hover:opacity-100 text-theme-success"
            />
          )}
        </>
      )}
    </button>
  );
};

interface OperationsTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: StoppageSubTypeOption[];
  locationOptions: LocationDTO[];
  isLocationOptionsLoading: boolean;
  setRefreshFn?: (fn: () => void) => void;
}

const UNFILTERED_OPERATIONS = [{ key: 'Status', value: '' }, { key: 'MachineName', value: '' }];
const OperationsTable: React.FC<OperationsTableProps> = ({ selectedDate, selectedShift, stoppageTypeGroupOptions, locationOptions, isLocationOptionsLoading, setRefreshFn }) => {
  const { t } = useTranslation("production");
  const { enqueueSnackbar } = useSnackbar();
  const isReadOnly = useIsReadOnly();
  const isStoppageReadOnly = useIsReadOnly("Stoppages");
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { nodVarianceThresholdHours } = useOperationsConfig();
  const refreshNestedTableRef = useRef<(() => void) | null>(null);
  const refreshNestedTable = useCallback(() => {
    if (refreshNestedTableRef.current) refreshNestedTableRef.current();
    else console.warn("Tried to refresh nested row before it was ready");
  }, []);
  const [prevStoppageTypeGroupOptions, setPrevStoppageTypeGroupOptions] = useState(stoppageTypeGroupOptions);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusOptions = ['UP', ...Array.from(new Set(stoppageTypeGroupOptions.map(o => o.stoppageType)))];
  const [tableFilters, setTableFilters] = useState<FilterConfig[]>(() => seedMachineNameFilter([
    {
      key: 'Status',
      label: t("operationsTable.statuses"),
      type: 'dropdown',
      addAllValuesSelector: true,
      valueIndex: 0,
      possibleValues: statusOptions,
      possibleValueLabel: machineStatusName,
      placeholder: t("operationsTable.filterByStatus"),
      value: ''
    },
    {
      key: 'MachineName',
      label: t("operationsTable.machines"),
      type: 'text',
      valueIndex: 1,
      placeholder: t("operationsTable.filterByMachine"),
      value: ''
    },
  ], readOperationsDeepLink(searchParams).machine));

  const [highlightedPlantId, setHighlightedPlantId] = useState<number | null>(
    () => readOperationsDeepLink(searchParams).plantId
  );

  useEffect(() => {
    const link = readOperationsDeepLink(searchParams);
    if (!hasOperationsDeepLink(link)) return;

    const remaining = new URLSearchParams(searchParams);
    OPERATIONS_DEEP_LINK_PARAMS.forEach(key => remaining.delete(key));
    setSearchParams(remaining, { replace: true });
  }, [searchParams, setSearchParams]);
  const { options: jobTypes } = useOptions<Option<number>, Option<number>>(
    ['references', 'jobTypes'],
    () => referencesAjax.getJobTypes(),
    o => o,
  );

  const getRows = useCallback(async (request: DataTableFilteredRequest) => {
    const filterValues = tableFilters.reduce((acc, cur) => {
      acc[cur.key] = cur.value ?? '';
      return acc;
    }, {} as Record<string, string>);
    const filters = { ...filterValues, pageNumber: 0, pageSize: 10 };

    const fromCache = () => {
      const status = filterValues['Status'] ?? '';
      const machineName = filterValues['MachineName'] ?? '';
      return filterCachedRows<StoppageCalc>(
        ['dataTable', 'operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift,
          UNFILTERED_OPERATIONS, 1, 5, null, 'asc', false, null],
        row => (!status || row.status === status) && matchesText(row.machineName, machineName),
        {
          field: request.sortField,
          direction: request.sortDirection,
          accessors: {
            Machine: row => row.machineName,
            Status: row => row.status,
          },
          fallback: row => row.machineName,
        },
      );
    }

    const result = await operationsAjax.getStoppageCalcsData(selectedDate, selectedShift, filters).catch((error: unknown) => {
      if (error === Cancelled) throw error;
      return null;
    });
    return result && !ErrorResponse.isErrorResponse(result) ? result : fromCache();
  }, [selectedDate, selectedShift, tableFilters]);

  const warmedSignatureRef = useRef<string | null>(null);
  const prefetchStoppages = useEffectEvent((entries: StoppageCalc[]) => {
    if (!isOnline) return;
    const dateIso = selectedDate.toISODate();
    if (!dateIso) return;

    const signature = plantStatusSignature(dateIso, selectedShift, entries);
    if (warmedSignatureRef.current === signature) return;
    warmedSignatureRef.current = signature;

    void warmPlantStoppagesCache(queryClient, selectedDate, selectedShift, entries);
  });

  const {
    rows,
    fetchedRows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    refresh,
    updateRowSource,
    removeRowSource,
    mutateCachedResult,
  } = useDataTable<StoppageCalc>({
    queryKey: ['operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift, tableFilters.map(f => ({ key: f.key, value: f.value }))],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: 5,
  });

  useEffect(() => {
    if (fetchedRows) prefetchStoppages(fetchedRows);
  }, [fetchedRows]);

  const updateEngineHours = useCallback(async (operationId: number, startHours: number, endHours: number, fuel: number | null) => {
    const response = await engineHoursMutations.updateEngineHours(operationId, startHours, endHours, fuel)

    if (response instanceof ErrorResponse) {
      enqueueSnackbar(t("operationsTable.updateFailed"), { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    if (!response.data) {
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(row => row.operationId === operationId
          ? new StoppageCalc({ ...row, startEngineHours: startHours, endEngineHours: endHours, fuel: fuel ?? undefined })
          : row),
      }));
      enqueueSnackbar(t("operationsTable.hoursSavedOffline"), { variant: 'info', autoHideDuration: 5000 })
      return true;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);

    enqueueSnackbar(t("operationsTable.hoursUpdated"), { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar, mutateCachedResult, updateRowSource, t])

  const updateLocation = useCallback(async (operationId: number, locationId: number) => {
    const response = await operationLocationMutations.updateOperationLocation(operationId, locationId);

    if (response instanceof ErrorResponse) {
      enqueueSnackbar(t("operationsTable.updateFailed"), { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    if (!response.data) {
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(row => row.operationId === operationId ? { ...row, locationId } : row),
      }));
      enqueueSnackbar(t("operationsTable.locationSavedOffline"), { variant: 'info', autoHideDuration: 5000 })
      return true;
    }

    updateRowSource((row) => row.plantId === response.data?.plantId, response.data!);
    enqueueSnackbar(t("operationsTable.locationUpdated"), { variant: 'success', autoHideDuration: 5000 })

    return true;
  }, [enqueueSnackbar, mutateCachedResult, updateRowSource, t])

  const refreshPlant = useCallback(async (plantId: number) => {
    if (!isOnline) return true;

    const response = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, plantId)
    if (response instanceof ErrorResponse) {
      enqueueSnackbar(t("operationsTable.refreshFailed"), { variant: 'error', autoHideDuration: 5000 })
      return false;
    }

    updateRowSource((row) => row.plantId === response.plantId, response);
    return true;
  }, [selectedDate, selectedShift, enqueueSnackbar, isOnline, updateRowSource, t]);

  const setAsAvailable = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.plantId || !stoppage.stoppageSubTypeId) {
      enqueueSnackbar(t("operationsTable.invalidStoppage"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid)
      return false;

    const reportFailure = (error: unknown) => {
      if (error !== Cancelled)
        enqueueSnackbar(t("operationsTable.refreshFailed"), { variant: 'error', autoHideDuration: 5000 });
      return null;
    };

    const response = await stoppagesMutations.updateStoppage(stoppage).catch(reportFailure);
    if (!response) return false;

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    enqueueSnackbar(t("operationsTable.statusUpdated"), { variant: 'success', autoHideDuration: 3000 })

    if (wasQueued(response)) {
      mirrorPlantStoppagesForStatus(stoppage.plantId, selectedDate.toISODate()!, selectedShift, 'UP');
      mutateCachedResult(old => ({
        ...old,
        data: old.data.map(r => r.plantId === stoppage.plantId
          ? { ...r, status: 'UP', stoppageId: undefined, stoppageColourHub: '', statusDescription: '', currentStoppageTypeId: undefined, currentStoppageSubTypeId: undefined }
          : r
        )
      }));

      const activeStatusFilter = tableFilters.find(filter => filter.key === 'Status')?.value;
      if (activeStatusFilter && activeStatusFilter !== 'UP') {
        removeRowSource(row => row.plantId === stoppage.plantId);
      }
    } else {
      const updatedOperationResponse = await operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, stoppage.plantId).catch(reportFailure);
      if (!updatedOperationResponse) return false;

      if (updatedOperationResponse instanceof ErrorResponse) {
        enqueueSnackbar(t("operationsTable.refreshFailed"), { variant: 'error', autoHideDuration: 5000 })

        return false;
      }

      updateRowSource((row) => row.plantId === updatedOperationResponse.plantId, updatedOperationResponse);
    }

    refreshNestedTable();
    return true;
  }, [enqueueSnackbar, selectedDate, selectedShift, refreshNestedTable, tableFilters, mutateCachedResult, removeRowSource, updateRowSource, t]);

  const translateRows = useCallback((r: StoppageCalc): TableRow => {
    return {
      id: r.plantId,
      values: [
        {
          name: 'Status',
          label: t("fields.status"),
          type: 'custom',
          value: r.status,
          tailwindClasses: `basis-[8%] justify-center`,
          render: (value) => {
            return (
              <span
                style={{
                  backgroundColor: r.status !== "UP" ? r.stoppageColourHub : ""
                }}
                className={`px-1 py-3 text-white font-bold h-full inline-flex w-full items-center justify-center ${r.status === "UP" ? 'bg-theme-success' : ""}`}>{machineStatusName(value.value)}</span>
            );
          },
        },
        {
          name: 'Toggle Available', label: t("operationsTable.toggleAvailable"), type: 'custom', value: 'custom', tailwindClasses: 'basis-[6%]',
          render: (_, row) => r.status !== 'UP' && <StatusCell
            rowId={row.id}
            status={r.status}
            disabled={isStoppageReadOnly}
            onClick={async () => {
              const now = DateTime.now();
              let merged = selectedDate.set({
                hour: now.hour,
                minute: now.minute,
                second: now.second,
                millisecond: now.millisecond
              });
              const startedAt = r.timeIn || now;
              if (merged < startedAt) merged = merged.plus({ days: 1 });
              await setAsAvailable(new StoppageDto({
                stoppageId: r.stoppageId,
                plantId: r.plantId,
                startTime: startedAt,
                endTime: merged,
                incident: false,
                stoppageTypeId: r.currentStoppageTypeId ?? 0,
                stoppageSubTypeId: r.currentStoppageSubTypeId ?? 0,
                locationId: r.locationId
              }));
            }}
          />
        },
        {
          name: 'Machine', label: t("fields.machine"), sortable: true, type: 'string', value: r.machineName, tailwindClasses: 'basis-[8%] py-3',
          render: (val) => <ZoneMachineCell name={val.value} zone={r.zone} target={machineLinkTarget(r, jobTypes)} />
        },
        {
          name: 'Start Engine Hours', label: t("operationsTable.startEngineHours"), editable: true, type: 'decimal', value: r.startEngineHours, tailwindClasses: 'basis-[8%] py-3', disabled: isReadOnly,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, newValue ?? 0, row.values.find(v => v.name === "End Engine Hours")?.value, row.values.find(v => v.name === "Fuel")?.value ?? null);
          }
        },
        {
          name: 'End Engine Hours', label: t("operationsTable.endEngineHours"), editable: true, type: 'decimal', value: r.endEngineHours, tailwindClasses: 'basis-[8%] py-3', disabled: isReadOnly,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, row.values.find(v => v.name === "Start Engine Hours")?.value, newValue ?? 0, row.values.find(v => v.name === "Fuel")?.value ?? null);
          }
        },
        {
          name: 'Fuel', label: t("operationsTable.fuel"), editable: true, type: 'decimal', value: r.fuel, tailwindClasses: 'basis-[6%] py-3', disabled: isReadOnly,
          onBlur: async (row, newValue) => {
            await updateEngineHours(r.operationId, row.values.find(v => v.name === "Start Engine Hours")?.value, row.values.find(v => v.name === "End Engine Hours")?.value, newValue ?? null);
          }
        },
        { name: 'Status Description', label: t("operationsTable.statusDescription"), type: 'string', value: machineStatusDescription(r.status, r.statusDescription), tailwindClasses: 'basis-[9%] py-3' },
        { name: 'Time In', label: t("columns.timeIn"), type: 'string', value: r.timeIn ? (DateTime.isDateTime(r.timeIn) ? r.timeIn : DateTime.fromISO(r.timeIn as unknown as string, { setZone: true })).toLocal().toFormat('HH:mm') : undefined, tailwindClasses: 'basis-[6%] py-3' },
        {
          name: "Avail'",
          label: t("operationsTable.availability"),
          type: 'percentage',
          value: formatFixed(r.availabilityPercentage, 2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Total Hours",
          label: t("operationsTable.totalHours"),
          type: 'integer',
          value: formatFixed(r.totalHours, 2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Standby",
          label: t("operationsTable.standby"),
          type: 'integer',
          value: r.calculatedNod ?? t("operationsTable.notAvailable"),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "NOD Diff'",
          label: t("operationsTable.nodDifference"),
          type: 'custom',
          value: r.calculatedNod ?? "N/A",
          tailwindClasses: 'basis-[5%] py-3',
          render: () => {
            if (!r.endEngineHours) return <span />;

            const diff = Math.abs((r.calculatedNod ?? 0) - ((r.standby ?? 0) + (r.nonOperatingDelay ?? 0) / 60));
            const isDanger = diff > nodVarianceThresholdHours;

            return (
              <span className={`px-2 py-1 rounded ${isDanger ? "bg-theme-danger text-white" : ""}`}>
                {t("operationsTable.hours", { value: formatFixed(diff, 2) })}
              </span>
            );
          }
        },
        {
          name: "UofA",
          label: t("operationsTable.utilisationOfAvailability"),
          type: 'percentage',
          value: formatFixed(r.utilizationOfAvailability, 2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Asset Util'",
          label: t("operationsTable.assetUtilisation"),
          type: 'percentage',
          value: formatFixed(r.assetUtilization, 2),
          tailwindClasses: 'basis-[5%] py-3'
        },
        {
          name: "Location", label: t("fields.location"), type: 'custom', value: r.locationId, tailwindClasses: 'basis-[11%] py-3', render: (tableValue) =>
            <Autocomplete
              value={locationOptions.find(o => o.id === tableValue.value) ?? null}
              classes={{ root: "w-full transition" }}
              loading={isLocationOptionsLoading}
              disabled={isLocationOptionsLoading || isReadOnly}
              options={locationOptions}
              getOptionLabel={(o) => o.name}
              onChange={(_, opt) => { updateLocation(r.operationId, opt?.id ?? 0) }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={t("operationsTable.selectLocation")}
                  size="small"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps.input,
                      endAdornment: (
                        <>
                          {isLocationOptionsLoading ? <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span> : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
        },
      ]
    }
  }, [isReadOnly, isStoppageReadOnly, selectedDate, setAsAvailable, jobTypes, updateEngineHours, locationOptions, isLocationOptionsLoading, updateLocation, nodVarianceThresholdHours, t]);

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  if (stoppageTypeGroupOptions !== prevStoppageTypeGroupOptions) {
    setPrevStoppageTypeGroupOptions(stoppageTypeGroupOptions);
    setTableFilters(prev => withStatusOptions(prev, statusOptions));
  }

  const { pulseRowKey } = useScrollAndPulseRow(
    highlightedPlantId ? String(highlightedPlantId) : null,
    !isLoading,
    useCallback(() => setHighlightedPlantId(null), [])
  );

  const updateTableFilters = useCallback((filters: SetStateAction<FilterConfig[]>) => {
    setTableFilters(filters);
  }, []);

  useEffect(() => {
    if (setRefreshFn) setRefreshFn(() => refresh);
  }, [refresh, setRefreshFn]);

  useEffect(() => {
    if (!connection) return;

    const handleDataUpdated = async (jsonData: string) => {
      const plantIdsToUpdate = plantIdsToRefresh(jsonData, selectedDate.toISODate());
      if (plantIdsToUpdate.length === 0) return;

      const results = await Promise.all(plantIdsToUpdate.map(plantId =>
        operationsAjax.getStoppageCalcsDataForPlant(selectedDate, selectedShift, plantId)
      )).catch((error: unknown) => {
        if (error !== Cancelled) console.error("Failed to refresh the plants a live update named", error);
        return null;
      });
      if (!results) return;

      const updatedRowDtos = results.filter((dto): dto is StoppageCalc => !(dto instanceof ErrorResponse));

      if (updatedRowDtos.length !== results.length) {
        enqueueSnackbar(t("operationsTable.liveUpdatesInterrupted"), { variant: 'error', autoHideDuration: 5000 });
        return;
      }

      for (const updatedRowDto of updatedRowDtos) {
        updateRowSource(
          oldRow => oldRow.plantId === updatedRowDto.plantId,
          updatedRowDto
        );
      }
      refreshNestedTable();

      const message = describeUpdatedMachines(updatedRowDtos.map(dto => dto.machineName));
      if (message) enqueueSnackbar(message, { variant: 'info', autoHideDuration: 5000 });
    };

    connection.on("DataUpdatedAsync", handleDataUpdated);

    return () => {
      connection.off("DataUpdatedAsync", handleDataUpdated);
    };
  }, [connection, refreshNestedTable, selectedDate, selectedShift, enqueueSnackbar, updateRowSource, t]);

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      pulseRowKey={pulseRowKey}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      tableFilters={tableFilters}
      setTableFilters={updateTableFilters}
      minTableWidthPixels={1440}
      maxTableWidthPixels={1800}
      disableContainer={true}
      virtualizeOver={20}
      fillHeight
      expandedRowRenderer={row => {
        return (
          <NestedOperationsTable
            plantId={row.id}
            machineName={row.values.find(v => v.name === 'Machine')?.value}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            stoppageTypeGroupOptions={stoppageTypeGroupOptions}
            parentRowStatus={row.values.find(v => v.name === 'Status')?.value}
            refreshParent={refreshPlant}
            registerRefreshFn={(fn: () => void) => {
              refreshNestedTableRef.current = fn;
            }}
          />
        )
      }}
    />
  )
};

export default OperationsTable;
